import { useState } from 'react';
import { fmtCompact, fmtMoney } from '../utils/format';

export interface CompositionSlice {
  id: string;
  label: string;
  value: number;
  color: string;
}

interface Props {
  slices: CompositionSlice[];
  /** Show labels inside slices that are at least this wide (% of total). */
  inlineLabelThreshold?: number;
  ariaLabel?: string;
}

export function CompositionBar({ slices, inlineLabelThreshold = 0.13, ariaLabel }: Props) {
  const [hover, setHover] = useState<string | null>(null);
  const total = slices.reduce((s, x) => s + Math.max(0, x.value), 0);
  if (total <= 0) return null;

  return (
    <div className="space-y-2">
      <div
        role="img"
        aria-label={ariaLabel}
        className="flex h-12 w-full overflow-hidden rounded-xl ring-1 ring-ink-100 dark:ring-ink-700"
      >
        {slices.map(s => {
          if (s.value <= 0) return null;
          const pct = s.value / total;
          const showInline = pct >= inlineLabelThreshold;
          const isHover = hover === s.id;
          return (
            <button
              type="button"
              key={s.id}
              onMouseEnter={() => setHover(s.id)}
              onMouseLeave={() => setHover(null)}
              onFocus={() => setHover(s.id)}
              onBlur={() => setHover(null)}
              className="group relative flex items-center justify-center overflow-hidden text-[11px] font-semibold text-white transition-all"
              style={{
                flexBasis: `${pct * 100}%`,
                backgroundColor: s.color,
                filter: isHover || hover == null ? 'none' : 'saturate(0.6) opacity(0.75)',
              }}
              title={`${s.label}: ${fmtMoney(s.value)} (${(pct * 100).toFixed(1)}%)`}
            >
              {showInline && (
                <span className="truncate px-2 font-mono tabular-nums drop-shadow-sm">
                  {fmtCompact(s.value)}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 sm:grid-cols-3">
        {slices.map(s => {
          const pct = s.value / total;
          const isHover = hover === s.id;
          return (
            <div
              key={s.id}
              className={`flex items-baseline gap-2 text-[11px] transition ${
                isHover ? 'opacity-100' : hover ? 'opacity-60' : 'opacity-100'
              }`}
              onMouseEnter={() => setHover(s.id)}
              onMouseLeave={() => setHover(null)}
            >
              <span
                className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
                style={{ backgroundColor: s.color }}
              />
              <span className="min-w-0 flex-1 truncate text-ink-700 dark:text-ink-100">{s.label}</span>
              <span className="shrink-0 font-mono tabular-nums text-ink-500 dark:text-ink-300">
                {(pct * 100).toFixed(0)}%
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
